import React, { useState, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ReactNode;
  isPro?: boolean;
  badge?: string;
  children?: NavigationItem[];
}

interface NavigationSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  color: string;       // Tailwind color class for the section accent
  bgActive: string;    // Active background for items in this section
  items: NavigationItem[];
  collapsible?: boolean;
}

// SVG icon components — clean, consistent, no emoji
const Icons = {
  home: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955a1.126 1.126 0 011.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
    </svg>
  ),
  megaphone: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46" />
    </svg>
  ),
  target: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9 9 0 100-18 9 9 0 000 18z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 15a3 3 0 100-6 3 3 0 000 6z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 12h.008v.008H12V12z" />
    </svg>
  ),
  heart: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
    </svg>
  ),
  chart: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
  ),
  cog: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  // Sub-item icons
  campaign: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
    </svg>
  ),
  whatsapp: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  ),
  users: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-1.997M18 8.625a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM16.5 3h.008v.008H16.5V3z" />
    </svg>
  ),
  route: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
    </svg>
  ),
  bolt: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
    </svg>
  ),
  cube: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
    </svg>
  ),
  shoppingBag: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
    </svg>
  ),
  ticket: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 010 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 010-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375z" />
    </svg>
  ),
  book: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
    </svg>
  ),
  lightbulb: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
    </svg>
  ),
  link: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
    </svg>
  ),
  team: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
    </svg>
  ),
  billing: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
    </svg>
  ),
  brand: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
    </svg>
  ),
  chevronDown: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  ),
  sparkles: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
    </svg>
  ),
  proposal: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  ),
  userActivation: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

// Routes that have been removed — filter from recent pages
const REMOVED_ROUTES = ['/user-activation', '/debug-env', '/system-test', '/superadmin-usage-limits'];

// Adaptive sidebar: track page visits in localStorage
function getRecentPages(): string[] {
  try {
    const raw = localStorage.getItem('sidebar_recent_pages');
    const pages: string[] = raw ? JSON.parse(raw) : [];
    return pages.filter(p => !REMOVED_ROUTES.includes(p));
  } catch { return []; }
}
function recordPageVisit(path: string) {
  if (REMOVED_ROUTES.includes(path)) return;
  try {
    const recent = getRecentPages().filter(p => p !== path);
    recent.unshift(path);
    localStorage.setItem('sidebar_recent_pages', JSON.stringify(recent.slice(0, 5)));
  } catch { /* ignore */ }
}

const LifecycleSidebar: React.FC<SidebarProps> = ({ sidebarOpen, setSidebarOpen }) => {
  const location = useLocation();
  // Lazy init: only compute initial value once, not every render
  const [recentPages, setRecentPages] = useState<string[]>(() => getRecentPages());

  // Record page visits for adaptive sidebar
  React.useEffect(() => {
    if (location.pathname !== '/dashboard' && location.pathname !== '/') {
      recordPageVisit(location.pathname);
      setRecentPages(getRecentPages());
    }
  }, [location.pathname]);

  // Track which sections are expanded
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    reach: true,
    convert: true,
    retain: true,
    intelligence: true,
    analytics: false,
    settings: false,
  });
  // Track sections the user has manually collapsed so auto-expand doesn't fight them
  const userCollapsedRef = useRef<Set<string>>(new Set());

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const isCurrentlyOpen = prev[sectionId];
      if (isCurrentlyOpen) userCollapsedRef.current.add(sectionId);
      else userCollapsedRef.current.delete(sectionId);
      return { ...prev, [sectionId]: !isCurrentlyOpen };
    });
  };

  const isActive = (href: string): boolean => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/home';
    }
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const isSectionActive = (section: NavigationSection): boolean => {
    return section.items.some(item =>
      isActive(item.href) || (item.children?.some(child => isActive(child.href)) ?? false)
    );
  };

  const navigationSections: NavigationSection[] = useMemo(() => [
    {
      id: 'reach',
      title: 'Reach',
      icon: Icons.megaphone,
      color: 'violet',
      bgActive: 'bg-violet-500/20 text-violet-100',
      collapsible: true,
      items: [
        { name: 'Campaigns', href: '/campaign-wizard', icon: Icons.campaign },
        { name: 'WhatsApp', href: '/whatsapp-hub', icon: Icons.whatsapp },
        { name: 'Leads', href: '/lead-management', icon: Icons.users, isPro: true },
        { name: 'Segments', href: '/segment-builder', icon: Icons.users },
        { name: 'Email', href: '/email-integration', icon: Icons.campaign, isPro: true },
      ]
    },
    {
      id: 'convert',
      title: 'Convert',
      icon: Icons.target,
      color: 'blue',
      bgActive: 'bg-blue-500/20 text-blue-100',
      collapsible: true,
      items: [
        { name: 'Orders', href: '/order-management', icon: Icons.cube },
        { name: 'Billing', href: '/account-management', icon: Icons.billing },
        { name: 'Inventory', href: '/product-catalog', icon: Icons.shoppingBag },
        { name: 'Purchases', href: '/purchase-vendor', icon: Icons.proposal },
        { name: 'Cart Recovery', href: '/cart-recovery', icon: Icons.bolt },
        { name: 'Coupons', href: '/coupons', icon: Icons.proposal },
        { name: 'Stores', href: '/store-management', icon: Icons.brand },
      ]
    },
    {
      id: 'retain',
      title: 'Retain',
      icon: Icons.heart,
      color: 'emerald',
      bgActive: 'bg-emerald-500/20 text-emerald-100',
      collapsible: true,
      items: [
        { name: 'Customers', href: '/customer-crm', icon: Icons.users },
        { name: 'Support', href: '/support-center', icon: Icons.ticket },
        { name: 'Reviews', href: '/reviews', icon: Icons.lightbulb },
        { name: 'Shipping', href: '/shipping', icon: Icons.cube },
        { name: 'Loyalty', href: '/loyalty', icon: Icons.sparkles },
        { name: 'Knowledge Base', href: '/vector-database', icon: Icons.book },
      ]
    },
    {
      id: 'intelligence',
      title: 'Intelligence',
      icon: Icons.chart,
      color: 'cyan',
      bgActive: 'bg-cyan-500/20 text-cyan-100',
      collapsible: true,
      items: [
        { name: 'Ask AICOS', href: '/ask-aicos', icon: Icons.sparkles, badge: 'AI' },
        { name: 'Customer 360', href: '/customer-intelligence', icon: Icons.users, badge: 'New' },
        { name: 'Governance', href: '/governance', icon: Icons.cog, badge: 'New' },
        { name: 'Data Sources', href: '/data-sources', icon: Icons.link, badge: 'New' },
      ]
    },
    {
      id: 'automation',
      title: 'Automation',
      icon: Icons.bolt,
      color: 'amber',
      bgActive: 'bg-amber-500/20 text-amber-100',
      collapsible: true,
      items: [
        { name: 'Agents', href: '/agents', icon: Icons.sparkles, badge: 'New' },
        { name: 'Flow Builder', href: '/flow-builder', icon: Icons.bolt, badge: 'Chat' },
        { name: 'Workflows', href: '/workflow-automation', icon: Icons.cog, badge: 'Events' },
      ]
    },
    {
      id: 'analytics',
      title: 'Analytics',
      icon: Icons.chart,
      color: 'gray',
      bgActive: 'bg-gray-500/20 text-gray-100',
      collapsible: true,
      items: [
        { name: 'Dashboard', href: '/unified-analytics', icon: Icons.chart },
      ]
    },
    {
      id: 'settings',
      title: 'Settings',
      icon: Icons.cog,
      color: 'gray',
      bgActive: 'bg-gray-500/20 text-gray-100',
      collapsible: true,
      items: [
        { name: 'Brand', href: '/brands', icon: Icons.brand },
        { name: 'Integrations', href: '/integrations', icon: Icons.link, isPro: true },
        { name: 'Team', href: '/team-management', icon: Icons.team, isPro: true },
      ]
    },
  ], []);

  // Auto-expand section containing active route — but respect user's manual collapse
  React.useEffect(() => {
    for (const section of navigationSections) {
      if (
        isSectionActive(section) &&
        !expandedSections[section.id] &&
        !userCollapsedRef.current.has(section.id)
      ) {
        setExpandedSections(prev => ({ ...prev, [section.id]: true }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const sectionColors: Record<string, { dot: string; text: string; activeBg: string; hoverBg: string }> = {
    violet: { dot: 'bg-violet-400', text: 'text-violet-300', activeBg: 'bg-violet-500/20 text-white', hoverBg: 'hover:bg-violet-500/10' },
    blue:   { dot: 'bg-blue-400',   text: 'text-blue-300',   activeBg: 'bg-blue-500/20 text-white',   hoverBg: 'hover:bg-blue-500/10' },
    emerald:{ dot: 'bg-emerald-400', text: 'text-emerald-300', activeBg: 'bg-emerald-500/20 text-white', hoverBg: 'hover:bg-emerald-500/10' },
    cyan:   { dot: 'bg-cyan-400',   text: 'text-cyan-300',   activeBg: 'bg-cyan-500/20 text-white',   hoverBg: 'hover:bg-cyan-500/10' },
    amber:  { dot: 'bg-amber-400',  text: 'text-amber-300',  activeBg: 'bg-amber-500/20 text-white',  hoverBg: 'hover:bg-amber-500/10' },
    gray:   { dot: 'bg-gray-400',   text: 'text-gray-300',   activeBg: 'bg-gray-500/20 text-white',   hoverBg: 'hover:bg-gray-500/10' },
  };

  return (
    <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-gray-900 via-gray-900 to-gray-800 transform ${
      sidebarOpen ? 'translate-x-0' : '-translate-x-full'
    } transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 shadow-2xl flex flex-col`}>

      {/* Skip to content — accessibility */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-white focus:text-gray-900 focus:rounded-lg focus:shadow-lg focus:text-sm focus:font-medium">
        Skip to main content
      </a>

      {/* Header */}
      <div className="flex items-center justify-between h-16 px-5 border-b border-gray-700/50">
        <div className="flex items-center">
          <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-blue-500 rounded-lg flex items-center justify-center mr-3">
            <span className="text-white font-bold text-sm">AI</span>
          </div>
          <div>
            <h1 className="text-white text-sm font-bold leading-tight">Commerce OS</h1>
            <p className="text-gray-400 text-[10px] font-medium tracking-wide uppercase">Lifecycle Platform</p>
          </div>
        </div>
        <button
          className="lg:hidden text-gray-400 hover:text-white p-1.5 rounded-md transition-colors"
          onClick={() => setSidebarOpen(false)}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Home / Dashboard — always visible */}
      <div className="px-3 pt-4 pb-2">
        <Link
          to="/dashboard"
          className={`flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
            isActive('/dashboard')
              ? 'bg-white/10 text-white shadow-sm'
              : 'text-gray-300 hover:bg-white/5 hover:text-white'
          }`}
        >
          <span className="mr-3 text-gray-400">{Icons.home}</span>
          Home
        </Link>
      </div>

      {/* Recently Used — adaptive */}
      {recentPages.length > 0 && (
        <div className="px-3 pb-2">
          <p className="px-3 py-1 text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Recent</p>
          {recentPages.slice(0, 3).map(path => {
            const pageName = path.replace(/^\//, '').replace(/-/g, ' ').replace(/\//g, ' > ');
            const displayName = pageName.charAt(0).toUpperCase() + pageName.slice(1);
            return (
              <Link
                key={path}
                to={path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center px-3 py-1.5 text-xs rounded-md transition-all duration-150 ${
                  isActive(path) ? 'text-white bg-white/10' : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
                }`}
              >
                <span className="w-1 h-1 rounded-full bg-gray-600 mr-2.5" />
                <span className="truncate">{displayName}</span>
              </Link>
            );
          })}
        </div>
      )}

      {/* Navigation Sections */}
      <nav className="flex-1 px-3 pb-4 space-y-1 overflow-y-auto scrollbar-thin">
        {navigationSections.map((section) => {
          const colors = sectionColors[section.color] || sectionColors.gray;
          const isExpanded = expandedSections[section.id];
          const hasActiveChild = isSectionActive(section);

          return (
            <div key={section.id} className="pt-2">
              {/* Section Header */}
              <button
                onClick={() => toggleSection(section.id)}
                aria-expanded={isExpanded}
                aria-controls={`section-${section.id}`}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-colors ${
                  hasActiveChild ? colors.text : 'text-gray-500'
                } hover:text-gray-300`}
              >
                <div className="flex items-center">
                  <span className={`w-1.5 h-1.5 rounded-full ${colors.dot} mr-2.5`} />
                  {section.title}
                </div>
                <span className={`transform transition-transform duration-200 ${isExpanded ? 'rotate-0' : '-rotate-90'}`}>
                  {Icons.chevronDown}
                </span>
              </button>

              {/* Section Items */}
              {isExpanded && (
                <div id={`section-${section.id}`} role="region" aria-label={section.title} className="mt-0.5 space-y-0.5">
                  {section.items.map((item) => (
                    <div key={item.href}>
                      <Link
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-150 ${
                          isActive(item.href)
                            ? colors.activeBg
                            : `text-gray-400 ${colors.hoverBg} hover:text-gray-200`
                        }`}
                      >
                        <span className={`mr-3 ${isActive(item.href) ? 'text-white' : 'text-gray-500 group-hover:text-gray-400'}`}>
                          {item.icon}
                        </span>
                        <span className="flex-1 truncate">{item.name}</span>
                        {item.badge && (
                          <span className="ml-auto px-1.5 py-0.5 text-[10px] font-medium bg-gray-700/50 text-gray-400 rounded">
                            {item.badge}
                          </span>
                        )}
                        {item.isPro && !item.badge && (
                          <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold bg-amber-500/10 text-amber-400 rounded border border-amber-500/20">
                            PRO
                          </span>
                        )}
                      </Link>

                      {/* Children (sub-items) */}
                      {item.children && isActive(item.href) && (
                        <div className="ml-7 mt-0.5 space-y-0.5 border-l border-gray-700/50 pl-3">
                          {item.children.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href}
                              onClick={() => setSidebarOpen(false)}
                              className={`flex items-center px-2 py-1.5 text-xs font-medium rounded-md transition-all duration-150 ${
                                isActive(child.href)
                                  ? 'text-white bg-white/10'
                                  : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
                              }`}
                            >
                              <span className="mr-2">{child.icon}</span>
                              <span className="truncate">{child.name}</span>
                              {child.isPro && (
                                <span className="ml-auto px-1 py-0.5 text-[9px] font-bold text-amber-400/70">PRO</span>
                              )}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer — Brand Copilot quick access */}
      <div className="px-3 py-3 border-t border-gray-700/50">
        <Link
          to="/brand-copilot"
          onClick={() => setSidebarOpen(false)}
          className={`flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
            isActive('/brand-copilot')
              ? 'bg-violet-500/20 text-violet-200'
              : 'text-gray-400 hover:bg-violet-500/10 hover:text-violet-300'
          }`}
        >
          <span className="mr-3">{Icons.sparkles}</span>
          AI Copilot
          <span className="ml-auto text-[10px] text-gray-500 font-mono">Ctrl+/</span>
        </Link>
      </div>
    </div>
  );
};

export default LifecycleSidebar;
